import { Component, forwardRef, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  Validator,
  AbstractControl,
  ValidationErrors,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin, Observable, tap } from 'rxjs';

import { ButtonSize, ButtonStyle } from '@constants/button/1.0';
import { MessageType } from '@constants/message/1.0';
import { Composite } from '@interfaces/composite/1.0';
import { CouponItem, CouponItemsDialogData } from '@interfaces/coupon/1.0';
import { Product } from '@interfaces/product/1.0';
import { CompositeService } from '@services/composite/1.0/composite.service';
import { ProductService } from '@services/product/1.0/product.service';
import { UtilitiesService } from '@services/utilities/1.0/utilities.service';
import { LoadingColor, LoadingType } from '@constants/loading/1.0';
import { CouponItemsDialogComponent } from './coupon-items-dialog/coupon-items-dialog.component';
import { ModifierService } from '@services/modifier/1.0/modifier.service';
import { Modifier } from '@interfaces/modifier/1.0';
import { NgIf, NgFor } from '@angular/common';
import { LoadingComponent } from '../loading/1.0/loading.component';
import { MatFormField, MatLabel, MatHint, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { TxpIntegersOnlyDirective } from '../../directives/integers-only/1.0/txp-integers-only.directive';
import { ButtonComponent } from '../button/1.0/button.component';
import { MessageComponent } from '../message/1.0/message.component';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'txp-items-cva',
  templateUrl: './items-cva.component.html',
  styleUrls: ['./items-cva.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ItemsCvaComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ItemsCvaComponent),
      multi: true,
    },
  ],
  standalone: true,
  imports: [
    NgIf,
    LoadingComponent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    TxpIntegersOnlyDirective,
    MatHint,
    MatError,
    ButtonComponent,
    NgFor,
    MessageComponent,
    TranslateModule,
  ],
})
export class ItemsCvaComponent implements ControlValueAccessor, Validator, OnInit, OnChanges {
  @Input() formSubmitted: boolean;
  @Input() itemsRequired: boolean = true;
  @Input() disableCustomerGets = false;
  @Input() disableQty: boolean = true;
  @Input() hideQty: boolean = false;

  buttonStyle = ButtonStyle; // expose enum to template
  buttonSize = ButtonSize; // expose enum to template
  messageType = MessageType; // expose enum to template
  loadingColor = LoadingColor; // expose enum to template
  loadingType = LoadingType; // expose enum to template
  form: UntypedFormGroup;
  productLookup: { [catalogEntryId: string]: Product };
  productsList: Product[];
  comboLookup: { [catalogEntryId: string]: Composite };
  combosList: Composite[];
  modifierLookup: { [catalogEntryId: string]: Modifier };
  modifiersList: Modifier[];
  combinedData;
  loadProductsError = false;
  loadItemsError = false;
  brandId: string;
  itemsList: CouponItem[] = [];
  isLoading = false;
  private initialValue: any;

  constructor(
    private productService: ProductService,
    private compositeService: CompositeService,
    private modifierService: ModifierService,
    private utilitiesService: UtilitiesService,
    private dialog: MatDialog,
    private formBuilder: FormBuilder,
  ) {
    this.brandId = window.sessionStorage.getItem('brand_id');
  }

  ngOnInit() {
    this.isLoading = true;
    this.form = this.formBuilder.group({
      quantity: [
        { value: this.disableQty ? 1 : '', disabled: this.disableQty },
        [Validators.required, Validators.min(1)],
      ],
      itemIds: ['', this.itemsRequired ? Validators.required : null],
    });
    if (this.initialValue) {
      this.setValues(this.initialValue);
    }
    this.loadItems();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.formSubmitted) {
      this.validate();
    }
    if (changes.disableQty) {
      this.form?.get('quantity').setValue(this.disableQty ? 1 : '');
      this.disableQty ? this.form?.get('quantity').disable() : this.form?.get('quantity').enable();
    }
  }

  addItemsDialog(dialogType) {
    const data: CouponItemsDialogData = {
      dialogType,
      selectedItems: this.itemsList || [],
      productsList: this.productsList,
      combosList: this.combosList,
      modifiersList: this.modifiersList,
      productLookup: this.productLookup,
      comboLookup: this.comboLookup,
      modifierLookup: this.modifierLookup,
      brandId: this.brandId,
    };

    const matDialogRef = this.dialog.open(CouponItemsDialogComponent, { data });

    matDialogRef.afterClosed().subscribe((itemsList) => {
      // Preserve PLU property if present and store full list in form value
      this.itemsList = itemsList
        .map((item) => ({ ...item }))
        .sort((a, b) => a.name.localeCompare(b.name));
      this.form.get('itemIds').setValue(itemsList?.map((item) => item.id));
      // Store the full itemsList (with PLU) in the form value for persistence
      this.form.patchValue({ itemsList: this.itemsList });
      this.form.updateValueAndValidity();
      this.onChange(this.form?.getRawValue());
    });
  }

  loadItems(): void {
    this.isLoading = true;

    forkJoin({
      products: this.getProducts(),
      combos: this.getCombos(),
      modifiers: this.getModifiers(),
    }).subscribe({
      next: (data) => {
        let combinedData = [];

        // Iterate over products and combos
        [...data.products, ...data.combos, ...data.modifiers].forEach((item) => {
          combinedData.push({
            id: item.catalogEntryId,
            type: item.entityType,
            name: item.name,
          });
        });

        this.combinedData = combinedData;
        this.setValues(this.form?.getRawValue());
        this.isLoading = false;
      },
      error: (error) => {
        this.loadItemsError = true;
        this.isLoading = false;
      },
    });
  }

  /**
   * get Product API to obtain a list of Products
   */
  getProducts(): Observable<Product[]> {
    this.productsList = [];
    this.productLookup = {};

    return this.productService.getProducts(this.brandId).pipe(
      tap((products: Product[]) => {
        this.productsList = this.productsList.concat(products);
        Object.assign(
          this.productLookup,
          this.utilitiesService.convertArrayToMap(products, 'catalogEntryId'),
        );
      }),
    );
  }

  /**
   * Get the list of combos
   *
   * @param brandId - Brand ID to pull combos for
   */
  getCombos(): Observable<Composite[]> {
    this.combosList = [];
    this.comboLookup = {};
    return this.compositeService.getComposites(this.brandId).pipe(
      tap((combos: Composite[]) => {
        this.combosList = this.combosList.concat(combos);
        Object.assign(
          this.comboLookup,
          this.utilitiesService.convertArrayToMap(combos, 'catalogEntryId'),
        );
      }),
    );
  }

  getModifiers(): Observable<Modifier[]> {
    this.modifiersList = [];
    this.modifierLookup = {};
    return this.modifierService.getModifiers(this.brandId).pipe(
      tap((modifiers: Modifier[]) => {
        this.modifiersList = this.modifiersList.concat(modifiers);
        Object.assign(
          this.modifierLookup,
          this.utilitiesService.convertArrayToMap(modifiers, 'catalogEntryId'),
        );
      }),
    );
  }

  private onChange: any = () => {};
  private onTouch: any = () => {};

  writeValue(value: any): void {
    this.initialValue = value;
    this.setValues(value);
  }

  setValues(value) {
    const { quantity = this.disableQty ? 1 : '', itemIds = [] } = value || {};
    this.itemsList = [] as unknown as CouponItem[];
    this.form?.patchValue({ quantity, itemIds });
    if (value?.itemIds) {
      // Always use the full itemsList (with PLU) if present
      if (value.itemsList && Array.isArray(value.itemsList)) {
        this.itemsList = value.itemsList
          .map((item) => ({ ...item }))
          .sort((a, b) => a.name.localeCompare(b.name));
      } else {
        this.itemsList = this.combinedData
          ?.filter((item) => value.itemIds.includes(item.id))
          .sort((a, b) => a.name.localeCompare(b.name));
      }
    }
  }

  onQuantityChange() {
    this.form.updateValueAndValidity();

    this.form.markAllAsTouched();
    this.onChange(this.form?.getRawValue());
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
    this.onChange(this.form?.getRawValue());
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  setDisabledState?(isDisabled: boolean): void {}

  validate(control?: AbstractControl): ValidationErrors | null {
    if (!this.form) {
      return null;
    }
    if (this.formSubmitted) {
      this.form?.markAllAsTouched();
    }
    if (
      this.itemsRequired &&
      !this.itemsList?.length &&
      !this.form?.get('itemIds')?.value?.length
    ) {
      return { required: true };
    }
    return this.form?.valid ? null : { invalidItems: true };
  }
}
